import { ValidationFormat } from "@wix/ambassador-forms-v4-form/types";
import { getCountryByShortKey } from "@wix/locale-dataset-javascript";
import { applyValidationProperties } from "./apply-validation-properties.js";
import { applyViewProperties } from "./apply-view-properties.js";
import { ADDRESS_FORM_ID_BY_COUNTRY, DEFAULT_COUNTRY } from "./constants.js";
import { COUNTRY_CODES } from "./country-codes.js";
import { getCountryField } from "./get-country-field.js";
import { mapSubdivisionReplacedBy } from "./map-subdivision-replaced-by.js";
class MultilineAddressToolkit {
  constructor(addressForms, addressFormIdByCountry = ADDRESS_FORM_ID_BY_COUNTRY) {
    this.normalizeEmptyValues = (submission, form) => {
      return Object.keys(submission).reduce(
        (acc, key) => {
          const field = form.fields?.find((_field) => _field.target === key);
          if (field?.validation?.required && !field?.hidden && field?.validation?.predefined?.format === ValidationFormat.MULTILINE_ADDRESS) {
            acc[key] = submission[key] || {};
          } else {
            acc[key] = submission[key];
          }
          return acc;
        },
        {}
      );
    };
    this.addCountryField = (form, rootFormField) => {
      return {
        ...form,
        fields: [
          getCountryField(
            rootFormField.validation?.required && !rootFormField?.hidden,
            rootFormField.validation?.predefined?.multilineAddressOptions
          ),
          ...form.fields ?? []
        ]
      };
    };
    this.addSubdivisions = (form, countryCode) => {
      if (!countryCode) {
        return form;
      }
      const country = getCountryByShortKey(countryCode);
      const countryCodeAlpha3 = countryCode && countryCode in COUNTRY_CODES ? COUNTRY_CODES[countryCode] : void 0;
      const subdivisions = mapSubdivisionReplacedBy(
        country?.subdivisions?.list || [],
        countryCodeAlpha3
      );
      if (!subdivisions?.length) {
        return form;
      }
      return {
        ...form,
        fields: form.fields?.map((field) => {
          if (field?.target === "subdivision") {
            return {
              ...field,
              view: {
                ...field.view,
                options: subdivisions.map((subdivision) => ({
                  value: subdivision.key,
                  label: subdivision.displayName
                }))
              },
              validation: {
                ...field.validation,
                string: {
                  ...field.validation?.string,
                  enum: subdivisions.map((subdivision) => subdivision.key)
                }
              }
            };
          }
          return field;
        })
      };
    };
    this.getFormForValidation = (rootFormField, submissionValue) => {
      const country = this.getCountry(submissionValue);
      return this.getFormByCountry(country, rootFormField);
    };
    this.addressFormByCountry = this.mapFormsByCountry(
      addressFormIdByCountry,
      addressForms
    );
  }
  getFormByCountry(country, rootFormField) {
    this.assertMultiLineAddressField(rootFormField);
    const addressForm = country && this.addressFormByCountry[country] ? this.addressFormByCountry[country] : this.addressFormByCountry[DEFAULT_COUNTRY];
    if (!addressForm) {
      throw new Error(`No form suitable fo country: ${country}`);
    }
    return rootFormField ? this.addCountryField(
      this.addSubdivisions(
        this.applyRootFormSettingsOverrides(addressForm, rootFormField),
        country
      ),
      rootFormField
    ) : addressForm;
  }
  applyRootFormSettingsOverrides(addressForm, rootFormField) {
    const modifications = rootFormField ? [
      applyViewProperties(rootFormField.view),
      applyValidationProperties(
        rootFormField.validation?.predefined?.multilineAddressOptions
      )
    ] : [];
    return modifications.reduce((acc, func) => func(acc), addressForm);
  }
  assertMultiLineAddressField(rootFormField) {
    if (rootFormField && rootFormField.validation?.predefined?.format !== ValidationFormat.MULTILINE_ADDRESS) {
      throw new Error(
        `Only field ${ValidationFormat.MULTILINE_ADDRESS} is supported`
      );
    }
  }
  mapFormsByCountry(formIdByCountryMap, addressForms) {
    const formsById = addressForms.reduce(
      (acc, form) => {
        acc[form.id] = form;
        return acc;
      },
      {}
    );
    return Object.fromEntries(
      Object.entries(formIdByCountryMap).map(([country, formId]) => {
        const form = formsById[formId];
        if (form) {
          return [country, form];
        } else {
          throw new Error(`Missing form template for country ${country}`);
        }
      })
    );
  }
  getCountry(value) {
    if (value && typeof value === "object" && "country" in value) {
      return value.country;
    }
    return DEFAULT_COUNTRY;
  }
}
export {
  MultilineAddressToolkit
};
//# sourceMappingURL=multiline-address-toolkit.js.map
